import Node from '../node.js';
import Utils3D from '../../utils/utils3D.js';
import Utils from '../utils.js';
import Spline from '../spline.js';
import { PARAM_TYPE, PARAM_UNITS } from '../connector.js';

export default class NodeComputeBBox extends Node
{
	constructor(pParent)
	{
		super(pParent);

		this.addInput(PARAM_TYPE.GEOMETRY, 'geo');

		this.addInput(PARAM_TYPE.FLOAT, 'grow_width', 0.0, { minValue: -1.0, maxValue: 1.0, units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'grow_height', 0.0, { minValue: -1.0, maxValue: 1.0, units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'grow_depth', 0.0, { minValue: -1.0, maxValue: 1.0, units: PARAM_UNITS.METERS });

		this.addInput(PARAM_TYPE.FLOAT, 'move_x', 0.0, { minValue: -1.5, maxValue: 1.5, units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'move_y', 0.0, { minValue: -1.5, maxValue: 1.5, units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'move_z', 0.0, { minValue: -1.5, maxValue: 1.5, units: PARAM_UNITS.METERS });

		this.addOutput(PARAM_TYPE.BBOX, 'bbox');

		this.addOutput(PARAM_TYPE.FLOAT, 'width');
		this.addOutput(PARAM_TYPE.FLOAT, 'height');
		this.addOutput(PARAM_TYPE.FLOAT, 'depth');

		this.addOutput(PARAM_TYPE.FLOAT, 'center_x');
		this.addOutput(PARAM_TYPE.FLOAT, 'center_y');
		this.addOutput(PARAM_TYPE.FLOAT, 'center_z');

		this.addOutput(PARAM_TYPE.FLOAT, 'min_x');
		this.addOutput(PARAM_TYPE.FLOAT, 'min_y');
		this.addOutput(PARAM_TYPE.FLOAT, 'min_z');

		this.addOutput(PARAM_TYPE.FLOAT, 'max_x');
		this.addOutput(PARAM_TYPE.FLOAT, 'max_y');
		this.addOutput(PARAM_TYPE.FLOAT, 'max_z');

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo_out');
	}
	
	getType()
	{
		return 'computeBBox';
	}
	
	getValueFromBBox(bbox, outputName)
	{
		let finalValue = bbox;
		if(outputName === 'width')
			finalValue = bbox.maximumWorld.x - bbox.minimumWorld.x;
		else if(outputName === 'height')
			finalValue = bbox.maximumWorld.y - bbox.minimumWorld.y;
		else if(outputName === 'depth')
			finalValue = bbox.maximumWorld.z - bbox.minimumWorld.z;
		else if(outputName === 'center_x')
			finalValue = (bbox.maximumWorld.x + bbox.minimumWorld.x)/2.0;
		else if(outputName === 'center_y')
			finalValue = (bbox.maximumWorld.y + bbox.minimumWorld.y)/2.0;
		else if(outputName === 'center_z')
			finalValue = (bbox.maximumWorld.z + bbox.minimumWorld.z)/2.0;
		else if(outputName === 'min_x')
			finalValue = bbox.minimumWorld.x;
		else if(outputName === 'min_y')
			finalValue = bbox.minimumWorld.y;
		else if(outputName === 'min_z')
			finalValue = bbox.minimumWorld.z;
		else if(outputName === 'max_x')
			finalValue = bbox.maximumWorld.x;
		else if(outputName === 'max_y')
			finalValue = bbox.maximumWorld.y;
		else if(outputName === 'max_z')
			finalValue = bbox.maximumWorld.z;

		return finalValue;
	}

	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{
		if(outputName === 'geo_out')
			return inputValues['geo'];

		let meshList = [];
		let curveList = [];
		let bboxList = [];

		let pInputs = this.getInputs();
		for(let pInput of pInputs)
		{
			let pList = inputValues[pInput.getName()];
			if(!pList)
				continue;

			if(!Array.isArray(pList))
				pList = [pList];

			if(pList.length === 0)
				continue;

			if(Utils.getIsMeshObject(pList[0]))
				meshList = meshList.concat(pList);
			else if(pList[0].isClosed !== undefined)
				pList.forEach((curveData) => { curveList.push(new Spline(curveData)); });
			else if(pList[0].maximumWorld && pList[0].minimumWorld)
				bboxList.push(pList[0]);
		}


		let bboxGeos = Utils3D.getObjectsBBoxWithChildren(meshList);
		let bboxCurves = Spline.getCurvesBBox(curveList);

		for(let bbox of bboxList)
			Utils3D.includeBBox(bboxGeos, bbox);

		if(meshList.length === 0 && curveList.length > 0)
		{
			bboxCurves.minimumWorld = new BABYLON.Vector3(bboxCurves.minimum.x, bboxCurves.minimum.y, 0);
			bboxCurves.maximumWorld = new BABYLON.Vector3(bboxCurves.maximum.x, bboxCurves.maximum.y, 0);

			// Handle the 3D curve case
			if(bboxCurves.minimum.z !== undefined)
			{
				bboxCurves.minimumWorld.z = bboxCurves.minimum.z;
				bboxCurves.maximumWorld.z = bboxCurves.maximum.z;
			}

			bboxCurves.minimumWorld.x -= inputValues['grow_width']/2;
			bboxCurves.maximumWorld.x += inputValues['grow_width']/2;

			bboxCurves.minimumWorld.y -= inputValues['grow_height']/2;
			bboxCurves.maximumWorld.y += inputValues['grow_height']/2;

			bboxCurves.minimumWorld.z -= inputValues['grow_depth']/2;
			bboxCurves.maximumWorld.z += inputValues['grow_depth']/2;

			return this.getValueFromBBox(bboxCurves, outputName);
		}

		if(curveList.length > 0)
		{
			bboxCurves.minimum.z = bboxGeos.minimumWorld.z;
			bboxCurves.maximum.z = bboxGeos.maximumWorld.z;

			Utils3D.includePoint(bboxCurves.minimum, bboxGeos.minimumWorld, bboxGeos.maximumWorld);
			Utils3D.includePoint(bboxCurves.maximum, bboxGeos.minimumWorld, bboxGeos.maximumWorld);
		}

		if(bboxGeos.maximumWorld.x === -Number.MAX_VALUE)
		{
			bboxGeos.maximumWorld = BABYLON.Vector3.Zero();
			bboxGeos.minimumWorld = BABYLON.Vector3.Zero();
		}

		// Expand
		bboxGeos.minimumWorld.x -= inputValues['grow_width']/2;
		bboxGeos.maximumWorld.x += inputValues['grow_width']/2;

		bboxGeos.minimumWorld.y -= inputValues['grow_height']/2;
		bboxGeos.maximumWorld.y += inputValues['grow_height']/2;

		bboxGeos.minimumWorld.z -= inputValues['grow_depth']/2;
		bboxGeos.maximumWorld.z += inputValues['grow_depth']/2;

		// Shift
		bboxGeos.minimumWorld.x += inputValues['move_x'];
		bboxGeos.maximumWorld.x += inputValues['move_x'];

		bboxGeos.minimumWorld.y += inputValues['move_y'];
		bboxGeos.maximumWorld.y += inputValues['move_y'];

		bboxGeos.minimumWorld.z += inputValues['move_z'];
		bboxGeos.maximumWorld.z += inputValues['move_z'];

		return this.getValueFromBBox(bboxGeos, outputName);
	}
}
